import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { Team } from '../../api/teams'
import { useTeamUsage } from '../../hooks/useTeams'
import { cn } from '../../lib/utils'
import { createNadDebug } from '../../utils/debugLog'

const log = createNadDebug('dw:postpaid-usage')

type Breakdown = 'project' | 'member'
const BREAKDOWNS: readonly Breakdown[] = ['project', 'member']

export default function PostpaidUsageSummary({
  team,
  projectId,
}: {
  team: Team
  projectId?: string
}) {
  const { t, i18n } = useTranslation('settings')
  const [breakdown, setBreakdown] = useState<Breakdown>('project')
  const usage = useTeamUsage(team.id, true, projectId)
  const formatNumber = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  )
  const formatDate = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeZone: 'UTC',
      }),
    [i18n.language],
  )

  useEffect(() => {
    log({
      event: 'mode-resolved',
      team_id: team.id,
      project_id: projectId ?? null,
      mode: 'postpaid',
    })
  }, [projectId, team.id])

  useEffect(() => {
    if (!usage.data) return
    log({
      event: 'period-loaded',
      team_id: team.id,
      project_id: projectId ?? null,
      period_start: usage.data.periodStart,
      period_end: usage.data.periodEnd,
      total_credits: usage.data.totalCredits,
    })
  }, [projectId, team.id, usage.data])

  if (usage.isLoading) {
    return (
      <p
        role="status"
        className="text-base text-muted-foreground"
        data-testid="postpaid-usage-loading"
      >
        {t('settingsModal.postpaidUsage.loading')}
      </p>
    )
  }
  if (usage.isError || !usage.data) {
    return (
      <p
        role="alert"
        className="text-base text-destructive"
        data-testid="postpaid-usage-error"
      >
        {t('settingsModal.postpaidUsage.loadFailed')}
      </p>
    )
  }

  const rows =
    breakdown === 'project' ? usage.data.projects : usage.data.members
  return (
    <section className="flex flex-col gap-5" data-testid="postpaid-usage">
      <div>
        <h4 className="text-lg font-semibold text-foreground" data-testid="postpaid-usage-team-name">{team.name}</h4>
        <p className="text-base text-muted-foreground">
          {t('settingsModal.postpaidUsage.description')}
        </p>
        <p
          className="mt-1 text-sm text-text-muted"
          data-testid="postpaid-usage-period"
        >
          {t('settingsModal.postpaidUsage.currentPeriod', {
            start: formatDate.format(
              new Date(`${usage.data.periodStartDate}T00:00:00Z`),
            ),
            end: formatDate.format(
              new Date(`${usage.data.periodEndDate}T00:00:00Z`),
            ),
          })}
        </p>
      </div>

      <div className="rounded-xl border border-border bg-surface-0 p-4">
        <p className="text-sm text-muted-foreground">
          {t('settingsModal.postpaidUsage.creditsUsed')}
        </p>
        <p
          className="mt-1 text-hero font-semibold tabular-nums text-foreground"
          data-testid="postpaid-usage-total"
        >
          {formatNumber.format(usage.data.totalCredits)}
        </p>
      </div>

      {!projectId && (
        <div
          className="inline-flex w-fit rounded-lg border border-input-border bg-input-bg p-1"
          role="group"
          aria-label={t('settingsModal.postpaidUsage.breakdown')}
        >
          {BREAKDOWNS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={breakdown === value}
              data-testid={`postpaid-usage-by-${value}`}
              onClick={() => setBreakdown(value)}
              className={cn(
                'h-7 rounded-lg px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
                breakdown === value
                  ? 'bg-surface-3 text-foreground'
                  : 'text-muted-foreground hover:bg-surface-2 hover:text-foreground',
              )}
            >
              {t(
                `settingsModal.postpaidUsage.by${value === 'project' ? 'Project' : 'Member'}`,
              )}
            </button>
          ))}
        </div>
      )}

      {!projectId && (
        <div
          className="overflow-hidden rounded-xl border border-border bg-surface-0"
          data-testid="postpaid-usage-breakdown"
        >
          {rows.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              {t('settingsModal.postpaidUsage.noUsage')}
            </p>
          ) : (
            rows.map((row, index) => (
              <div
                key={`${breakdown}:${index}`}
                className="flex items-center justify-between gap-4 border-b border-border-subtle px-4 py-3 last:border-b-0"
              >
                <span className="min-w-0 truncate text-sm text-foreground">
                  {row.label}
                </span>
                <span className="shrink-0 text-sm font-medium tabular-nums text-foreground">
                  {formatNumber.format(row.credits)}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </section>
  )
}
